import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * Bridal Gallery Component
 *
 * High-fashion bridal image gallery with thumbnail navigation, continuous looping,
 * two-digit counter, smooth zoom/pan with pointer events, and fullscreen toggle.
 * Built to follow core Shopify theme component patterns.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [thumbs]         - Array of thumbnail buttons.
 * @property {HTMLElement[]}       [slides]         - Array of slide containers.
 * @property {HTMLElement}         [stage]          - Main gallery image container.
 * @property {HTMLElement}         [viewport]       - Inner viewport bounding slides.
 * @property {HTMLElement}         [counterCurrent] - Two-digit active index span.
 * @property {HTMLElement}         [zoomLevel]      - Zoom percentage text display.
 * @property {HTMLButtonElement}   [zoomInBtn]      - Zoom in button (+).
 * @property {HTMLButtonElement}   [zoomOutBtn]     - Zoom out button (-).
 * @property {HTMLButtonElement}   [fullscreenBtn]  - Fullscreen toggle button.
 * @property {HTMLButtonElement}   [prevButton]     - Previous slide circular button.
 * @property {HTMLButtonElement}   [nextButton]     - Next slide circular button.
 *
 * @extends {Component<Refs>}
 */
export class BridalGallery extends Component {
  // ─── Constants ──────────────────────────────────────────────────────────────
  static MIN_ZOOM = 1.0;
  static MAX_ZOOM = 2.5;
  static ZOOM_STEP = 0.25;
  static SWIPE_THRESHOLD_PX = 40;

  // ─── Private State ─────────────────────────────────────────────────────────
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {number} */
  #zoom = BridalGallery.MIN_ZOOM;

  /** @type {number} */
  #panX = 0;

  /** @type {number} */
  #panY = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #startY = 0;

  /** @type {number | null} */
  #activePointerId = null;

  /** @type {number} */
  #swipeStartX = 0;

  /** @type {number} */
  #swipeStartY = 0;

  /** @type {number | null} */
  #rafId = null;

  /** @type {HTMLElement | null} */
  #activeDragImage = null;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    const slides = this.refs.slides;
    if (!slides || slides.length === 0) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Delegated click handling for thumbnails & controls (replaces N separate listeners)
    this.addEventListener('click', this.#handleClick, { signal });

    // Keyboard navigation (scoped to component focus tree)
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    // Theme Editor integration: navigate to slide when block is selected in admin
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    // Pointer events for swipe & drag panning on the viewport
    if (this.refs.viewport) {
      this.refs.viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
      this.refs.viewport.addEventListener('pointermove', this.#onPointerMove, { signal });
      this.refs.viewport.addEventListener('pointerup', this.#onPointerUp, { signal });
      this.refs.viewport.addEventListener('pointercancel', this.#onPointerUp, { signal });
    }

    // Fullscreen change events
    document.addEventListener('fullscreenchange', this.#onFullscreenChange, { signal });
    /** @type {any} */ (document).addEventListener?.('webkitfullscreenchange', this.#onFullscreenChange, { signal });

    // Initialize display state
    this.setZoom(BridalGallery.MIN_ZOOM);
    this.goTo(this.#currentIndex, false);
  }

  updatedCallback() {
    super.updatedCallback();

    // Re-verify index and sync UI when Theme Editor re-renders the DOM subtree
    const total = this.refs.slides?.length ?? 0;
    if (this.#currentIndex >= total) {
      this.#currentIndex = 0;
    }
    this.goTo(this.#currentIndex, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#abortController?.abort();
    this.#abortController = null;
    this.#activeDragImage = null;
  }

  // ─── Public Navigation & Zoom API ──────────────────────────────────────────

  /**
   * Navigates to a specific image index with continuous looping and UI updates.
   * @param {number} index - Target index.
   * @param {boolean} [shouldScrollThumb=true] - Whether to scroll the thumbnail into view.
   */
  goTo(index, shouldScrollThumb = true) {
    const slides = this.refs.slides;
    if (!slides || slides.length === 0) return;

    const total = slides.length;
    let targetIndex = index;

    // Continuous looping
    if (targetIndex < 0) {
      targetIndex = total - 1;
    } else if (targetIndex >= total) {
      targetIndex = 0;
    }

    // Reset zoom when switching images
    this.setZoom(BridalGallery.MIN_ZOOM);

    // Update slides
    slides.forEach((slide, idx) => {
      const isActive = idx === targetIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    // Update thumbnails
    if (this.refs.thumbs) {
      this.refs.thumbs.forEach((thumb, idx) => {
        const isActive = idx === targetIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (isActive && shouldScrollThumb) {
          thumb.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      });
    }

    // Update counter (format 01, 02...)
    if (this.refs.counterCurrent) {
      this.refs.counterCurrent.textContent = String(targetIndex + 1).padStart(2, '0');
    }

    this.#currentIndex = targetIndex;

    // Dispatch event for parent or analytical listeners
    this.dispatchEvent(
      new CustomEvent('bridal-gallery:change', {
        bubbles: true,
        detail: { index: targetIndex, total },
      })
    );
  }

  /**
   * Navigates to the previous image.
   */
  prev() {
    this.goTo(this.#currentIndex - 1);
  }

  /**
   * Navigates to the next image.
   */
  next() {
    this.goTo(this.#currentIndex + 1);
  }

  /**
   * Sets the image zoom level between MIN_ZOOM (100%) and MAX_ZOOM (250%).
   * Uses theme core `clamp` utility.
   * @param {number} level - Desired zoom level.
   */
  setZoom(level) {
    const rounded = Math.round(level * 100) / 100;
    const clamped = clamp(rounded, BridalGallery.MIN_ZOOM, BridalGallery.MAX_ZOOM);
    this.#zoom = clamped;

    if (clamped <= BridalGallery.MIN_ZOOM) {
      this.#panX = 0;
      this.#panY = 0;
    }

    if (this.refs.zoomLevel) {
      this.refs.zoomLevel.textContent = `${Math.round(clamped * 100)}%`;
    }

    if (this.refs.zoomOutBtn) {
      this.refs.zoomOutBtn.disabled = clamped <= BridalGallery.MIN_ZOOM;
    }

    if (this.refs.zoomInBtn) {
      this.refs.zoomInBtn.disabled = clamped >= BridalGallery.MAX_ZOOM;
    }

    const currentSlide = this.refs.slides?.[this.#currentIndex];
    if (!currentSlide) return;

    const img = /** @type {HTMLElement | null} */ (currentSlide.querySelector('.bridal-gallery-main-img'));
    if (!img) return;

    if (clamped > BridalGallery.MIN_ZOOM) {
      currentSlide.classList.add('is-zoomed');
      img.style.transform = `scale(${clamped}) translate(${this.#panX / clamped}px, ${this.#panY / clamped}px)`;
    } else {
      currentSlide.classList.remove('is-zoomed');
      img.style.transform = '';
    }
  }

  /**
   * Toggles fullscreen mode on the gallery element.
   */
  async toggleFullscreen() {
    const isFs = this.#isFullscreenActive() || this.classList.contains('is-fullscreen-fallback');

    if (isFs) {
      if (document.exitFullscreen) {
        await document.exitFullscreen().catch(() => {});
      } else {
        const doc = /** @type {Document & { webkitExitFullscreen?: () => void }} */ (document);
        doc.webkitExitFullscreen?.();
      }
      this.classList.remove('is-fullscreen-fallback');
      this.#updateFullscreenIcons(false);
    } else {
      if (typeof this.requestFullscreen === 'function') {
        try {
          await this.requestFullscreen();
          this.#updateFullscreenIcons(true);
        } catch {
          this.classList.add('is-fullscreen-fallback');
          this.#updateFullscreenIcons(true);
        }
      } else {
        const el = /** @type {HTMLElement & { webkitRequestFullscreen?: () => void }} */ (this);
        if (typeof el.webkitRequestFullscreen === 'function') {
          el.webkitRequestFullscreen();
          this.#updateFullscreenIcons(true);
        } else {
          // Fallback for Safari/iOS
          this.classList.add('is-fullscreen-fallback');
          this.#updateFullscreenIcons(true);
        }
      }
    }
  }

  // ─── Private Helpers & Handlers ─────────────────────────────────────────────

  /**
   * Helper to check if fullscreen is active across standard and vendor prefixes.
   * @returns {boolean}
   */
  #isFullscreenActive() {
    const doc = /** @type {Document & { webkitFullscreenElement?: Element }} */ (document);
    return Boolean(document.fullscreenElement || doc.webkitFullscreenElement);
  }

  /**
   * Updates fullscreen icon toggle states.
   * @param {boolean} isFullscreen
   */
  #updateFullscreenIcons(isFullscreen) {
    if (!this.refs.fullscreenBtn) return;
    const expand = /** @type {HTMLElement | null} */ (this.refs.fullscreenBtn.querySelector('.icon-expand'));
    const compress = /** @type {HTMLElement | null} */ (this.refs.fullscreenBtn.querySelector('.icon-compress'));
    if (expand && compress) {
      expand.style.display = isFullscreen ? 'none' : 'block';
      compress.style.display = isFullscreen ? 'block' : 'none';
    }
  }

  /**
   * Unified click handler utilizing event delegation.
   * Avoids registering N event listeners across individual buttons.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    // Thumbnail clicked
    const thumb = target.closest('.bridal-gallery-thumb');
    if (thumb && thumb instanceof HTMLElement) {
      event.preventDefault();
      const index = Number(thumb.dataset.index);
      if (!Number.isNaN(index)) {
        this.goTo(index);
      }
      return;
    }

    // Previous button clicked
    if (target.closest('[ref="prevButton"]')) {
      event.preventDefault();
      this.prev();
      return;
    }

    // Next button clicked
    if (target.closest('[ref="nextButton"]')) {
      event.preventDefault();
      this.next();
      return;
    }

    // Zoom in clicked
    if (target.closest('[ref="zoomInBtn"]')) {
      event.preventDefault();
      this.setZoom(this.#zoom + BridalGallery.ZOOM_STEP);
      return;
    }

    // Zoom out clicked
    if (target.closest('[ref="zoomOutBtn"]')) {
      event.preventDefault();
      this.setZoom(this.#zoom - BridalGallery.ZOOM_STEP);
      return;
    }

    // Fullscreen clicked
    if (target.closest('[ref="fullscreenBtn"]')) {
      event.preventDefault();
      this.toggleFullscreen();
    }
  };

  /**
   * Keyboard handler for arrow and escape navigation.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'Escape') {
      if (this.#zoom > BridalGallery.MIN_ZOOM) {
        this.setZoom(BridalGallery.MIN_ZOOM);
      }
    }
  };

  /**
   * Shopify Theme Editor block selection handler.
   * Automatically displays the slide corresponding to the block clicked in the editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const blockEl = target?.closest('[data-index]');
    if (blockEl instanceof HTMLElement && blockEl.dataset.index !== undefined) {
      const index = Number(blockEl.dataset.index);
      if (!Number.isNaN(index)) {
        this.goTo(index);
      }
    }
  };

  #onFullscreenChange = () => {
    const isFs = this.#isFullscreenActive();
    this.#updateFullscreenIcons(isFs);
    if (!isFs) {
      this.classList.remove('is-fullscreen-fallback');
    }
  };

  /**
   * Handles pointer down for touch/mouse swipe and zoom panning.
   * @param {PointerEvent} e
   */
  #onPointerDown = (e) => {
    if (this.#zoom <= BridalGallery.MIN_ZOOM) {
      this.#swipeStartX = e.clientX;
      this.#swipeStartY = e.clientY;
      return;
    }

    if (e.pointerType === 'mouse' && e.button !== 0) return;

    this.#isDragging = true;
    this.#activePointerId = e.pointerId;
    this.#startX = e.clientX - this.#panX;
    this.#startY = e.clientY - this.#panY;

    if (this.refs.viewport) {
      try {
        this.refs.viewport.setPointerCapture(e.pointerId);
      } catch {
        // Safe fallback
      }
    }

    const currentSlide = this.refs.slides?.[this.#currentIndex];
    currentSlide?.classList.add('is-dragging');
    this.#activeDragImage = /** @type {HTMLElement | null} */ (
      currentSlide?.querySelector('.bridal-gallery-main-img') ?? null
    );
  };

  /**
   * Handles pointer move for zoom panning with RAF throttling.
   * @param {PointerEvent} e
   */
  #onPointerMove = (e) => {
    if (!this.#isDragging || this.#zoom <= BridalGallery.MIN_ZOOM || !this.refs.viewport) return;

    const maxPanX = (this.refs.viewport.clientWidth * (this.#zoom - 1)) / 2;
    const maxPanY = (this.refs.viewport.clientHeight * (this.#zoom - 1)) / 2;

    const nextX = e.clientX - this.#startX;
    const nextY = e.clientY - this.#startY;

    this.#panX = clamp(nextX, -maxPanX, maxPanX);
    this.#panY = clamp(nextY, -maxPanY, maxPanY);

    if (this.#rafId === null) {
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        if (this.#activeDragImage && this.#isDragging) {
          this.#activeDragImage.style.transform = `scale(${this.#zoom}) translate(${this.#panX / this.#zoom}px, ${this.#panY / this.#zoom}px)`;
        }
      });
    }
  };

  /**
   * Handles pointer up / cancel for swipe navigation and drag release.
   * @param {PointerEvent} e
   */
  #onPointerUp = (e) => {
    if (this.#zoom <= BridalGallery.MIN_ZOOM) {
      const deltaX = e.clientX - this.#swipeStartX;
      const deltaY = e.clientY - this.#swipeStartY;
      if (Math.abs(deltaX) > BridalGallery.SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        if (deltaX < 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      return;
    }

    if (!this.#isDragging) return;
    this.#isDragging = false;

    if (this.refs.viewport && this.#activePointerId !== null) {
      try {
        this.refs.viewport.releasePointerCapture(this.#activePointerId);
      } catch {
        // Safe fallback
      }
      this.#activePointerId = null;
    }

    const currentSlide = this.refs.slides?.[this.#currentIndex];
    currentSlide?.classList.remove('is-dragging');
    this.#activeDragImage = null;
  };
}

if (!customElements.get('bridal-gallery')) {
  customElements.define('bridal-gallery', BridalGallery);
}
